import {
  Button,
  Checkbox,
  DatePicker,
  Input,
  Modal,
  Popover,
  Select,
  Space,
  Table,
  TableColumnType,
  Typography,
  Upload,
} from "antd";

import {
  DownloadOutlined,
  EditOutlined,
  FilterOutlined,
  PlusCircleOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { debounce } from "lodash";
import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import * as xlsx from "xlsx";
import HistoryTimeline from "../../components/history/HistoryTimeline";
import GoodsSearchIcon from "../../components/icons/GoodsSearch";
import ModalAddWarehouse from "../../components/modal/Warehouse/ModalAddWarehouse";
import ModalEditWarehouse from "../../components/modal/Warehouse/ModalEditWarehouse";
import { IPartNumberDtoResponse } from "../../models/Warehouse";
import { useGetHistoryByPartNumberId } from "../../query/HistoryQuery";
import { useImportWarehouse, useWarehouses } from "../../query/WarehouseQuery";
import { RootState } from "../../store";
import { renderQCStatusTag } from "../../utils/qcHelper";
import { notify } from "../../utils/toast";
import { warehouseService } from "../../services/warehouseService";
const Warehouse = () => {
  const [popoverFilters, setPopoverFilters] = useState<{
    awb: string;
    partNumber: string;
    projectCode: string;
  }>({
    awb: "",
    partNumber: "",
    projectCode: "",
  });

  const [columnFilters, setColumnFilters] = useState<{
    date?: string;
    project?: string;
    stock?: number;
  }>({});

  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const warehouse = useSelector(
    (state: RootState) => state.warehouse.locations,
  );
  const [openModalAddWarehouse, setOpenModalAddWarehouse] =
    useState<boolean>(false);
  const [openModalEdit, setOpenModalEdit] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<any | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>();
  const [locationFilter, setLocationFilter] = useState<string | undefined>();
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
    record?: any;
  }>({ visible: false, x: 0, y: 0 });
  const [openHistoryModal, setOpenHistoryModal] = useState<boolean>(false);
  const [historyPartNumberId, setHistoryPartNumberId] = useState<number | null>(
    null,
  );
  const { data: historyData, isLoading: isLoadingHistory } =
    useGetHistoryByPartNumberId(historyPartNumberId as number);
  const filterOptions = warehouse
    .filter((item) => item.warehouseCode !== "QC")
    .map((item) => ({
      label: item.warehouseCode,
      value: item.warehouseCode,
    }));
  const { data, isLoading } = useWarehouses({
    page: currentPage,
    limit: pageSize,
    ...popoverFilters,
    ...columnFilters,
    code: warehouseFilter,
    location: locationFilter,
  });

  const handleApplyFilter = () => {
    const newParams: any = {};
    if (popoverFilters.awb) {
      newParams.awb = popoverFilters.awb;
    }
    if (popoverFilters.partNumber) {
      newParams.partNumber = popoverFilters.partNumber;
    }
    if (popoverFilters.projectCode) {
      newParams.projectCode = popoverFilters.projectCode;
    }
    setPopoverFilters(newParams);
    setCurrentPage(1);
  };

  const handleClearFilter = () => {
    setPopoverFilters({
      awb: "",
      partNumber: "",
      projectCode: "",
    });
    setColumnFilters({});
    setCurrentPage(1);
  };

  const handleExportToExcel = () => {
    if (!data?.data?.length) {
      notify.error("Không có dữ liệu để xuất");
      return;
    }
    const currentPageData = data.data;
    const exportColumns = visibleColumns.filter((col) => col.key !== "action");
    const headers = exportColumns.map((col) => col.title as string);
    const rows = currentPageData.map((record: any) => {
      return exportColumns.map((col) => {
        const { dataIndex, key } = col;

        let value: any = "";
        if (Array.isArray(dataIndex)) {
          value = (dataIndex as (string | number)[]).reduce(
            (obj: any, k: any) => obj?.[k],
            record,
          );
        } else if (key === "location") {
          value = record.location?.code;
        } else if (typeof dataIndex === "string") {
          value = record[dataIndex];
        }
        if (value === null || value === undefined) return "";

        if (
          typeof value === "string" &&
          value.includes("T") &&
          value.includes("Z")
        ) {
          return new Date(value).toLocaleString();
        }

        return value;
      });
    });

    const ws = xlsx.utils.aoa_to_sheet([headers, ...rows]);

    const colWidths = headers.map((header, colIndex) => {
      const maxLength = Math.max(
        header.length,
        ...rows.map((row) => String(row[colIndex] || "").length),
      );

      return {
        wch: Math.min(maxLength + 2, 40),
      };
    });

    ws["!cols"] = colWidths;

    const wb = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(wb, ws, "Warehouse Data");

    const timestamp = new Date().toISOString().slice(0, 10);
    const fileName = `warehouse_page_${currentPage}_${timestamp}.xlsx`;

    xlsx.writeFile(wb, fileName);

    notify.success(`Xuất ${rows.length} dòng OK`);
  };

  const handleExportAllData = () => {
    Modal.confirm({
      title: "Xuất toàn bộ dữ liệu",
      content: (
        <div>
          <p>Tổng số bản ghi: {data?.pagination.total || 0}</p>
          <p>Bạn muốn xuất toàn bộ dữ liệu? Điều này có thể mất một lúc.</p>
        </div>
      ),
      okText: "Xuất",
      cancelText: "Hủy",
      onOk: async () => {
        try {
          notify.info("Đang tải dữ liệu để xuất...");
          const totalRecords = data?.pagination.total || 0;
          const allData = await warehouseService.getWarehouse({
            page: 1,
            limit: Math.max(totalRecords, 10000),
          });

          if (!allData?.data?.length) {
            notify.error("Không có dữ liệu để xuất");
            return;
          }

          const exportColumns = visibleColumns.filter(
            (col) => col.key !== "action",
          );
          const headers = exportColumns.map((col) => col.title as string);
          const rows = allData.data.map((record: any) => {
            return exportColumns.map((col) => {
              const { dataIndex, key } = col;

              let value: any = "";
              if (Array.isArray(dataIndex)) {
                value = (dataIndex as (string | number)[]).reduce(
                  (obj: any, k: any) => obj?.[k],
                  record,
                );
              } else if (key === "location") {
                value = record.location?.code;
              } else if (typeof dataIndex === "string") {
                value = record[dataIndex];
              }
              if (value === null || value === undefined) return "";

              if (
                typeof value === "string" &&
                value.includes("T") &&
                value.includes("Z")
              ) {
                return new Date(value).toLocaleString();
              }

              return value;
            });
          });
          const ws = xlsx.utils.aoa_to_sheet([headers, ...rows]);
          const colWidths = headers.map((header, colIndex) => {
            const maxLength = Math.max(
              header.length,
              ...rows.map((row: any) => String(row[colIndex] || "").length),
            );
            return {
              wch: Math.min(maxLength + 2, 40),
            };
          });
          ws["!cols"] = colWidths;
          const wb = xlsx.utils.book_new();
          xlsx.utils.book_append_sheet(wb, ws, "Warehouse Data");
          const timestamp = new Date().toISOString().slice(0, 10);
          const fileName = `warehouse_all_${timestamp}.xlsx`;
          xlsx.writeFile(wb, fileName);
          notify.success(`Xuất ${rows.length} dòng OK`);
        } catch (error) {
          notify.error("Lỗi khi xuất file");
          console.error(error);
        }
      },
    });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchChange = useCallback(
    debounce((field: string, value: string | number) => {
      setColumnFilters((prev) => {
        const updatedValue = field === "stock" && value ? Number(value) : value;
        return {
          ...prev,
          [field]: updatedValue || undefined,
        };
      });
      setCurrentPage(1);
    }, 300),
    [],
  );
  const importMutation = useImportWarehouse();
  const allColumns: TableColumnType<IPartNumberDtoResponse>[] = [
    // {
    //   title: "Action",
    //   key: "action",
    //   align: "center",
    //   width: 80,
    //   render: (_: any, record: any) => (
    //     <Button
    //       icon={<EditOutlined />}
    //       type="link"
    //       onClick={() => {
    //         setSelectedRecord(record);
    //         setOpenModalEdit(true);
    //       }}
    //     >
    //       Sửa
    //     </Button>
    //   ),
    // },
    {
      title: "Ngày",
      dataIndex: "importDate",
      key: "importDate",
      width: 120,
      align: "center",
      filterDropdown: () => {
        return (
          <div style={{ padding: 8 }}>
            <DatePicker
              placeholder="Chọn ngày"
              onChange={(date) => {
                handleSearchChange(
                  "date",
                  date ? date.format("YYYY-MM-DD") : "",
                );
              }}
              style={{ width: "100%", marginBottom: 8, display: "block" }}
            />
          </div>
        );
      },
      filterIcon: () => (
        <SearchOutlined
          style={{ color: columnFilters.date ? "#1890ff" : undefined }}
        />
      ),
    },
    {
      title: "QC Status",
      dataIndex: "qcStatus",
      key: "qcStatus",
      width: 110,
      align: "center",
      render: (_: string, record: any) => renderQCStatusTag(record.qcStatus),
    },
    {
      title: "Project",
      dataIndex: "project",
      key: "project",
      align: "center",
      width: 80,
      filterDropdown: () => {
        return (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="Tìm kiếm dự án"
              onChange={(e) => {
                handleSearchChange("project", e.target.value);
              }}
              style={{ marginBottom: 8, display: "block" }}
            />
          </div>
        );
      },
      filterIcon: () => (
        <SearchOutlined
          style={{ color: columnFilters.project ? "#1890ff" : undefined }}
        />
      ),
    },
    {
      title: "Mã dự án",
      dataIndex: "projectCode",
      key: "projectCode",
      align: "center",
      width: 80,
      filterDropdown: () => {
        return (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="Tìm kiếm mã dự án"
              onChange={(e) => {
                handleSearchChange("projectCode", e.target.value);
              }}
              style={{ marginBottom: 8, display: "block" }}
            />
          </div>
        );
      },
      filterIcon: () => (
        <SearchOutlined
          style={{ color: popoverFilters.projectCode ? "#1890ff" : undefined }}
        />
      ),
    },
    {
      title: "Part Number",
      dataIndex: "partNumber",
      key: "partNumber",
      width: 160,
      align: "center",
      filterDropdown: () => {
        return (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="Tìm kiếm mã hàng"
              onChange={(e) => {
                handleSearchChange("partNumber", e.target.value);
              }}
              style={{ marginBottom: 8, display: "block" }}
            />
          </div>
        );
      },
      filterIcon: () => (
        <SearchOutlined
          style={{ color: popoverFilters.partNumber ? "#1890ff" : undefined }}
        />
      ),
    },
    {
      title: "Nhà sản xuất",
      dataIndex: "manufacturer",
      key: "manufacturer",
      align: "center",
      width: 90,
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      key: "description",
      align: "center",
      width: 90,
      ellipsis: true,
    },
    {
      title: "Đơn vị tính",
      dataIndex: "unit",
      key: "unit",
      width: 100,
      align: "center",
    },
    {
      title: "Invoice Quantity",
      dataIndex: "invoiceQuantity",
      key: "invoiceQuantity",
      width: 140,
      align: "center",
    },
    {
      title: "Actual Quantity",
      dataIndex: "actualQuantity",
      key: "actualQuantity",
      width: 140,
      align: "center",
    },
    {
      title: "Stock",
      dataIndex: "stock",
      key: "stock",
      width: 70,
      align: "center",
      filterDropdown: () => {
        return (
          <div style={{ padding: 8 }}>
            <Input
              type="number"
              placeholder="Tìm kiếm stock"
              onChange={(e) => {
                handleSearchChange("stock", e.target.value);
              }}
              style={{ marginBottom: 8, display: "block" }}
            />
          </div>
        );
      },
      filterIcon: () => (
        <SearchOutlined
          style={{ color: columnFilters.stock ? "#1890ff" : undefined }}
        />
      ),
    },
    {
      title: "Stock QC",
      dataIndex: "stockQC",
      key: "stockQC",
      width: 80,
      align: "center",
    },
    {
      title: "AWB",
      dataIndex: "awb",
      key: "awb",
      width: 120,
      ellipsis: true,
      align: "center",
      filterDropdown: () => {
        return (
          <div style={{ padding: 8 }}>
            <Input
              placeholder="Tìm kiếm AWB"
              onChange={(e) => {
                handleSearchChange("awb", e.target.value);
              }}
              style={{ marginBottom: 8, display: "block" }}
            />
          </div>
        );
      },
      filterIcon: () => (
        <SearchOutlined
          style={{ color: popoverFilters.awb ? "#1890ff" : undefined }}
        />
      ),
    },
    {
      title: "Mã kho",
      dataIndex: ["location", "warehouse", "warehouseCode"],
      key: "warehouseCode",
      width: 80,
      align: "center",
      filterDropdown: () => {
        return (
          <div style={{ padding: "8px" }}>
            <Space direction="vertical">
              <Space wrap>
                {filterOptions.map((option) => (
                  <Button
                    key={option.value}
                    type={
                      warehouseFilter === option.value ? "primary" : "default"
                    }
                    onClick={() => {
                      setWarehouseFilter(
                        warehouseFilter === option.value
                          ? undefined
                          : option.value,
                      );
                      setCurrentPage(1);
                    }}
                    size="small"
                  >
                    {option.label}
                  </Button>
                ))}
              </Space>
              {warehouseFilter && (
                <Button
                  type="link"
                  danger
                  size="small"
                  onClick={() => {
                    setWarehouseFilter(undefined);
                    setCurrentPage(1);
                  }}
                >
                  Xóa bộ lọc
                </Button>
              )}
            </Space>
          </div>
        );
      },
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 90,
      align: "center",
      render: (_: any, record: any) => record.location?.code,
      filterDropdown: () => {
        const selectedWarehouse = warehouse.find(
          (w) => w.warehouseCode === warehouseFilter,
        );
        const availableLocations = selectedWarehouse?.warehouseLocations || [];

        return (
          <div style={{ padding: 8, minWidth: 250 }}>
            <div style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Kho</div>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Space wrap>
                  {warehouse
                    .filter((item) => item.warehouseCode !== "QC")
                    .map((w) => (
                      <Button
                        key={w.warehouseCode}
                        type={
                          warehouseFilter === w.warehouseCode
                            ? "primary"
                            : "default"
                        }
                        onClick={() => {
                          setWarehouseFilter(
                            warehouseFilter === w.warehouseCode
                              ? undefined
                              : w.warehouseCode,
                          );
                          setLocationFilter(undefined);
                          setCurrentPage(1);
                        }}
                        size="small"
                      >
                        {w.warehouseCode}
                      </Button>
                    ))}
                </Space>
              </Space>
            </div>

            {warehouseFilter && (
              <div>
                <div style={{ marginBottom: 8, fontWeight: 500 }}>Vị trí</div>
                <Select
                  placeholder="Chọn vị trí"
                  style={{ width: "100%" }}
                  value={locationFilter}
                  onChange={(value) => {
                    setLocationFilter(value);
                    setCurrentPage(1);
                  }}
                  options={availableLocations.map((loc) => ({
                    label: loc.code,
                    value: loc.code,
                  }))}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  allowClear
                  showSearch
                />
                {availableLocations.length === 0 && (
                  <span
                    style={{ color: "#999", marginTop: 8, display: "block" }}
                  >
                    Không có vị trí nào trong kho này
                  </span>
                )}
              </div>
            )}

            {!warehouseFilter && (
              <span style={{ color: "#999" }}>Vui lòng chọn kho trước</span>
            )}

            {warehouseFilter && (
              <div style={{ marginTop: 12 }}>
                <Button
                  type="link"
                  danger
                  size="small"
                  onClick={() => {
                    setWarehouseFilter(undefined);
                    setLocationFilter(undefined);
                    setCurrentPage(1);
                  }}
                >
                  Xóa bộ lọc
                </Button>
              </div>
            )}
          </div>
        );
      },
      filterIcon: () => (
        <SearchOutlined
          style={{
            color: warehouseFilter || locationFilter ? "#1890ff" : undefined,
          }}
        />
      ),
    },

    {
      title: "Mã nội bộ",
      dataIndex: "internalCode",
      key: "internalCode",
      width: 100,
      align: "center",
    },
    {
      title: "Ghi chú",
      dataIndex: "note",
      key: "note",
      width: 120,
      align: "center",
      ellipsis: true,
    },
    {
      title: "Ghi chú 2",
      dataIndex: "noteExt",
      key: "noteExt",
      width: 100,
      align: "center",
      ellipsis: true,
    },
    {
      title: "Pur",
      dataIndex: "purchaser",
      key: "purchaser",
      width: 80,
      align: "center",
    },
    {
      title: "Type",
      dataIndex: "supplierType",
      key: "supplierType",
      width: 80,
      align: "center",
    },
  ];
  const defaultCheckedList = allColumns.map((item) => item.key as string);
  const [checkedList, setCheckedList] = useState<string[]>(defaultCheckedList);

  const visibleColumns = allColumns.filter((col) =>
    checkedList.includes(col.key as string),
  );

  return (
    <div
      style={{
        padding: "16px",
        maxHeight: "100vh",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <Space style={{ flexWrap: "wrap" }}>
          <Popover
            title="Hiển thị/Ẩn cột"
            trigger="click"
            content={
              <div style={{ padding: "8px", maxWidth: 500 }}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Space>
                    <Button
                      type="default"
                      size="small"
                      onClick={() =>
                        setCheckedList(
                          allColumns.map((col) => col.key as string),
                        )
                      }
                    >
                      Chọn tất cả
                    </Button>
                    <Button
                      type="default"
                      size="small"
                      onClick={() => setCheckedList([])}
                    >
                      Bỏ chọn tất cả
                    </Button>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => setCheckedList(defaultCheckedList)}
                    >
                      Khôi phục mặc định
                    </Button>
                  </Space>
                  <div
                    style={{
                      maxHeight: "400px",
                      overflowY: "auto",
                      paddingRight: "8px",
                    }}
                  >
                    <Checkbox.Group
                      className="flex flex-col gap-2"
                      style={{ width: "100%" }}
                      value={checkedList}
                      onChange={(checkedValues) => {
                        setCheckedList(checkedValues as string[]);
                      }}
                    >
                      {allColumns.map((col) => (
                        <Checkbox key={col.key} value={col.key}>
                          <Typography.Text>
                            {col.title as string}
                          </Typography.Text>
                        </Checkbox>
                      ))}
                    </Checkbox.Group>
                  </div>
                </Space>
              </div>
            }
          >
            <Button>Tùy chỉnh cột hiển thị</Button>
          </Popover>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportToExcel}
            disabled={!data?.data || data.data.length === 0}
          >
            Xuất Excel
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportAllData}
            disabled={!data?.pagination.total || data.pagination.total === 0}
          >
            Xuất toàn bộ
          </Button>
          <Popover
            placement="bottom"
            title={null}
            trigger="click"
            content={
              <div
                style={{
                  maxHeight: 500,
                  overflow: "auto",
                }}
              >
                <div
                  style={{
                    border: "1px solid",
                    borderRadius: 4,
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "100px 200px 120px",
                      borderBottom: "2px solid",
                    }}
                  >
                    <div
                      style={{
                        padding: "8px 12px",
                        borderRight: "1px solid",
                      }}
                    >
                      Mã dự án
                    </div>
                    <div
                      style={{
                        padding: "8px 12px",
                        borderRight: "1px solid",
                      }}
                    >
                      Mã sản xuất
                    </div>
                    <div
                      style={{
                        padding: "8px 12px",
                      }}
                    >
                      AWB
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "100px 200px 120px",
                    }}
                  >
                    <div style={{ borderRight: "1px solid" }}>
                      <Input.TextArea
                        value={popoverFilters.projectCode}
                        onChange={(e) =>
                          setPopoverFilters({
                            ...popoverFilters,
                            projectCode: e.target.value,
                          })
                        }
                        bordered={false}
                        rows={10}
                        style={{
                          resize: "none",
                          fontSize: 12,
                        }}
                      />
                    </div>

                    <div style={{ borderRight: "1px solid" }}>
                      <Input.TextArea
                        value={popoverFilters.partNumber}
                        onChange={(e) =>
                          setPopoverFilters({
                            ...popoverFilters,
                            partNumber: e.target.value,
                          })
                        }
                        bordered={false}
                        rows={10}
                        style={{
                          resize: "none",
                          fontSize: 12,
                        }}
                      />
                    </div>

                    <div>
                      <Input.TextArea
                        value={popoverFilters.awb}
                        onChange={(e) =>
                          setPopoverFilters({
                            ...popoverFilters,
                            awb: e.target.value,
                          })
                        }
                        bordered={false}
                        rows={10}
                        style={{
                          resize: "none",
                          fontSize: 12,
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    paddingTop: 16,
                  }}
                >
                  <Button type="primary" block onClick={handleApplyFilter}>
                    Áp dụng bộ lọc
                  </Button>
                  <Button block onClick={handleClearFilter}>
                    Xoá tất cả
                  </Button>
                </div>
              </div>
            }
          >
            <Button icon={<FilterOutlined />}>Bộ lọc</Button>
          </Popover>
        </Space>
        <Space>
          <Button
            type="primary"
            icon={<PlusCircleOutlined />}
            onClick={() => setOpenModalAddWarehouse(true)}
          >
            Nhập kho
          </Button>
          <Upload
            disabled={userInfo?.role !== "SUPER_ADMIN"}
            accept=".xlsx,.xls,.csv"
            showUploadList={false}
            beforeUpload={(file) => {
              const isExcelOrCSV =
                file.type ===
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                file.type === "application/vnd.ms-excel" ||
                file.type === "text/csv" ||
                file.name.endsWith(".xlsx") ||
                file.name.endsWith(".xls") ||
                file.name.endsWith(".csv");
              if (!isExcelOrCSV) {
                notify.error("Chỉ chấp nhận file Excel hoặc CSV!");
                return Upload.LIST_IGNORE;
              }

              return true;
            }}
            customRequest={({ file: fileObj, onSuccess, onError }) => {
              const file = fileObj as File;
              const reader = new FileReader();
              reader.onload = (e) => {
                try {
                  const workbook = xlsx.read(e.target?.result, {
                    type: "array",
                  });
                  const firstSheetName = workbook.SheetNames[0];
                  const worksheet = workbook.Sheets[firstSheetName];
                  const data = xlsx.utils.sheet_to_json(worksheet);
                  const requiredFields = ["Tên", "Mã vị trí", "Mã kho"];
                  const firstRow = data[0] as any;
                  const missingFields = requiredFields.filter(
                    (field) => firstRow[field] === undefined,
                  );
                  if (missingFields.length > 0) {
                    notify.error(`File không hợp lệ`);
                    return;
                  }

                  importMutation.mutate(file, {
                    onSuccess: () => {
                      notify.success(`Đã import file ${file.name} thành công!`);
                      onSuccess?.("ok");
                    },
                    onError: (error) => {
                      const errorMessage =
                        error instanceof Error
                          ? error.message
                          : "Có lỗi khi import file. Vui lòng kiểm tra lại định dạng file.";
                      notify.error(errorMessage);
                      onError?.(new Error(errorMessage));
                    },
                  });
                } catch (error) {
                  notify.error(
                    "Lỗi khi kiểm tra file: " + (error as Error).message,
                  );
                  onError?.(error as Error);
                }
              };
              reader.onerror = () => {
                notify.error("Lỗi khi đọc file");
                onError?.(new Error("Error reading file"));
              };
              reader.readAsArrayBuffer(file);
              return {
                abort() {},
              };
            }}
          >
            <Button
              type="default"
              hidden={userInfo?.role !== "SUPER_ADMIN"}
              icon={<UploadOutlined />}
            >
              Nhập từ Excel, CSV
            </Button>
          </Upload>
        </Space>
      </div>
      <div className="w-full" style={{ overflowX: "auto", maxWidth: "100%" }}>
        <Table
          style={{
            border: "1px solid var(--border-color)",
            borderRadius: 8,
            overflow: "hidden",
          }}
          tableLayout="fixed"
          columns={visibleColumns}
          rowKey="id"
          onRow={(record) => ({
            onContextMenu: (e) => {
              e.preventDefault();
              setContextMenu({
                visible: true,
                x: e.clientX,
                y: e.clientY,
                record,
              });
            },
          })}
          dataSource={data?.data || []}
          loading={isLoading || importMutation.status === "pending"}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: data?.pagination.total || 0,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
          locale={{ emptyText: "Không có dữ liệu" }}
          scroll={{ x: "100%", y: 700 }}
        />

        {contextMenu.visible && (
          <div
            style={{
              position: "fixed",
              left: `${contextMenu.x}px`,
              top: `${contextMenu.y}px`,
              backgroundColor: "var(--bg-secondary)",
              border: "1px solid var(--border-color)",
              borderRadius: 4,
              boxShadow: "var(--shadow)",
              zIndex: 1000,
              minWidth: 180,
              color: "var(--text-primary)",
            }}
            onMouseLeave={() =>
              setContextMenu({ ...contextMenu, visible: false })
            }
          >
            <div
              style={{
                padding: "4px 0",
                cursor: "pointer",
              }}
              onClick={() => {
                setHistoryPartNumberId(contextMenu.record?.id);
                setOpenHistoryModal(true);
                setContextMenu({ ...contextMenu, visible: false });
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor =
                  "var(--bg-primary)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor =
                  "transparent";
              }}
            >
              <div
                style={{
                  padding: "8px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <GoodsSearchIcon />
                <span>Truy vết lịch sử</span>
              </div>
            </div>
            <div
              style={{
                padding: "4px 0",
                cursor: "pointer",
              }}
              onClick={() => {
                setSelectedRecord(contextMenu.record);
                setOpenModalEdit(true);
                setContextMenu({ ...contextMenu, visible: false });
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor =
                  "var(--bg-primary)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor =
                  "transparent";
              }}
            >
              <div
                style={{
                  padding: "8px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <EditOutlined />
                <span>Chỉnh sửa</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <ModalAddWarehouse
        open={openModalAddWarehouse}
        onCancel={() => setOpenModalAddWarehouse(false)}
        onSuccess={() => {}}
      />
      <ModalEditWarehouse
        open={openModalEdit}
        record={selectedRecord}
        onCancel={() => {
          setSelectedRecord(null);
          setOpenModalEdit(false);
        }}
      />

      <Modal
        title="Truy vết lịch sử"
        open={openHistoryModal}
        onCancel={() => {
          setOpenHistoryModal(false);
          setHistoryPartNumberId(null);
        }}
        footer={null}
        width="80%"
        style={{ maxHeight: "90vh" }}
        bodyStyle={{ maxHeight: "calc(90vh - 110px)", overflow: "auto" }}
      >
        {historyPartNumberId && (
          <HistoryTimeline
            title={`Lịch sử hàng ${historyData?.partNumber.partNumber}`}
            data={historyData?.history || []}
            isLoading={isLoadingHistory}
            isEmpty={!historyData?.history || historyData.history.length === 0}
            emptyMessage="Không có lịch sử cho hàng này"
          />
        )}
      </Modal>
    </div>
  );
};
export default Warehouse;
